import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from 'semantic-ui-react';
import { UserContext } from '../context/User';
import { StatusContext } from '../context/Status';
import { useSystemBrandingV9 } from '../context/SystemBrandingV9';
import { API, showError, showSuccess } from '../helpers';
import HeaderMenusV108 from './HeaderMenusV108';
import CommercialPreviewV106 from './CommercialPreviewV106';
import { auditLabel } from '../pages/AccessAuditV101/labels';
import Footer from './Footer';
import { staffTitleV110, staffWorkTitleV110 } from '../pages/StaffV110/staff';
import { isAuthPageV104 } from '../helpers/authSessionV104';
import { isClientAuthSurfaceV115 } from '../helpers/clientLoginV110';
import '../pages/ClientAuthorizeV110/style.css';
import {
  ACUVERSE_NAV_GROUPS_V9,
  buildAcuverseNavigationV9,
  matchAcuverseNavigationV9,
} from './navigationAcuverseV9';

const EMPTY_HIERARCHY_V9 = {
  ready: false,
  child: true,
  to: '/token',
  portalProfile: 'standard',
};

const AcuverseLayoutV9 = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [userState, userDispatch] = useContext(UserContext);
  const statusContext = useContext(StatusContext);
  const status = Array.isArray(statusContext)
    ? statusContext[0].status
    : undefined;
  const { systemName, logo, onLogoError } = useSystemBrandingV9();
  const { pathname, search: routeSearch } = useLocation();
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState(EMPTY_HIERARCHY_V9);
  const [mobile, setMobile] = useState(() => window.innerWidth <= 820);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const drawerRef = useRef(null);
  const toggleRef = useRef(null);
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const standaloneAuth = isClientAuthSurfaceV115(pathname, routeSearch);
  const authPage = standaloneAuth || isAuthPageV104(pathname);
  const consolePage =
    Boolean(userState.user) &&
    !authPage &&
    pathname !== '/about' &&
    pathname !== '/home';

  useEffect(() => {
    const updateWidth = () => setMobile(window.innerWidth <= 820);
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    const user = userState.user;
    if (authPage || !user || user.role >= 100 || user.account_kind_v110 === 'staff') {
      setHierarchy({
        ready: true,
        child: false,
        to: '/organization',
        portalProfile: 'standard',
      });
      return undefined;
    }
    setHierarchy(EMPTY_HIERARCHY_V9);
    API.get('/api/billing/v8/organization')
      .then((response) => {
        if (cancelled) return;
        const payload = response?.data?.data;
        if (!response?.data?.success || !payload?.configured) {
          setHierarchy({ ...EMPTY_HIERARCHY_V9, ready: true });
          return;
        }
        const accountID = payload.organization?.account?.id;
        const userID = payload.membership?.user_id || user.id;
        const child = payload.membership?.role !== 'owner';
        setHierarchy({
          ready: true,
          child,
          to: child
            ? '/organization/' + accountID + '/user/' + userID
            : '/organization/' + accountID,
          portalProfile:
            payload.organization?.account?.portal_profile || 'standard',
        });
      })
      .catch(() => {
        if (!cancelled) setHierarchy({ ...EMPTY_HIERARCHY_V9, ready: true });
      });
    return () => {
      cancelled = true;
    };
  }, [authPage, userState.user]);

  useEffect(() => {
    if (!mobile || !drawerOpen || !consolePage) return undefined;
    const previousOverflow = document.body.style.overflow;
    const toggle = toggleRef.current;
    const background = [contentRef.current, headerRef.current].filter(Boolean);
    background.forEach((element) => element.setAttribute('inert', ''));
    document.body.style.overflow = 'hidden';
    drawerRef.current?.querySelector('button, input, a')?.focus();
    return () => {
      background.forEach((element) => element.removeAttribute('inert'));
      document.body.style.overflow = previousOverflow;
      toggle?.focus();
    };
  }, [mobile, drawerOpen, consolePage]);

  const items = useMemo(
    () => [
      ...buildAcuverseNavigationV9({
        user: userState.user,
        hierarchy,
        chatEnabled: Boolean(status?.chat_link),
      }),
      ...(userState.user?.role >= 100 && hierarchy.ready
        ? [
            {
              name: 'access_audit_v101',
              to: '/access-audit-v101',
              icon: 'clipboard list',
              group: 'usage',
            },
          ]
        : []),
    ],
    [userState.user, hierarchy, status?.chat_link]
  );
  const activeItem = matchAcuverseNavigationV9(items, pathname);
  const navLabel = (name) =>
    name === 'coding_apps_v115' ? (i18n.language?.startsWith('zh') ? '编程软件' : 'Coding apps') :
    name === 'staff_admin_v110' ? staffTitleV110(i18n.language) :
    name === 'staff_workbench_v110' ? staffWorkTitleV110(i18n.language) :
    name === 'staff_security_v110' ? t('setting.tabs.security') :
    name === 'access_audit_v101' ? auditLabel('title', i18n.language) : t(name);
  const filteredItems = items.filter((item) =>
    navLabel(item.name)
      .toLocaleLowerCase()
      .includes(search.trim().toLocaleLowerCase())
  );

  const closeDrawer = () => setDrawerOpen(false);
  const handleDrawerKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab' || !mobile) return;
    const controls = Array.from(
      drawerRef.current.querySelectorAll(
        'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]'
      )
    );
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  const logout = async () => {
    setLoggingOut(true);
    try {
      const response = await API.get('/api/user/logout');
      if (!response?.data?.success) {
        showError(response?.data?.message || t('v9.logoutFailed'));
        return;
      }
      localStorage.removeItem('user');
      userDispatch({ type: 'logout' });
      showSuccess(t('v9.loggedOut'));
      closeDrawer();
      navigate('/login');
    } catch (_) {
      showError(t('v9.logoutFailed'));
    } finally {
      setLoggingOut(false);
    }
  };
  const brand = (
    <>
      <img src={logo} alt='' onError={onLogoError} />
      <span title={systemName}>{systemName}</span>
    </>
  );

  if (standaloneAuth) return <main id='client-auth-main-v115' className='client-auth-shell-v115'>{children}</main>;

  return (
    <div
      className={
        'ac-layout-v9 ' +
        (consolePage
          ? 'ac-console-v9'
          : authPage
          ? 'ac-public-v9 ac-auth-v9'
          : 'ac-public-v9')
      }
      data-edition='acuverse'
    >
      <a className='ac-skip-v9' href='#ac-main-v9'>
        {t('v9.skipContent')}
      </a>
      <header className='ac-topbar-v9' ref={headerRef}>
        {consolePage ? (
          <>
            {mobile ? (
              <button
                ref={toggleRef}
                type='button'
                className='ac-icon-button-v9'
                aria-label={t('header.open_menu')}
                aria-expanded={drawerOpen}
                aria-controls='ac-sidebar-v9'
                onClick={() => setDrawerOpen(true)}
              >
                <Icon name='bars' />
              </button>
            ) : null}
            {mobile ? (
              <Link className='ac-brand-v9' to='/home'>
                {brand}
              </Link>
            ) : (
              <div className='ac-breadcrumb-v9'>
                <Link to='/'>{t('v9.workspace')}</Link>
                <span aria-hidden='true'>/</span>
                <strong>
                  {activeItem ? navLabel(activeItem.name) : systemName}
                </strong>
              </div>
            )}
          </>
        ) : (
          <Link className='ac-brand-v9' to='/home'>
            {brand}
          </Link>
        )}
        <div className='ac-header-actions-v9 ac-header-actions-v108'>
          {consolePage && <CommercialPreviewV106 root={userState.user?.role >= 100} />}
          {userState.user && !consolePage && !authPage ? (
            <Link className='ac-text-link-v9' to='/'>
              {t('v9.workbench')}
            </Link>
          ) : null}
          <HeaderMenusV108
            user={userState.user}
            loggingOut={loggingOut}
            onLogout={logout}
          />
          {!userState.user && !authPage ? (
            <Link className='ac-text-link-v9' to='/login'>
              {t('header.login')}
            </Link>
          ) : null}
        </div>
      </header>
      {consolePage && (!mobile || drawerOpen) ? (
        <aside
          id='ac-sidebar-v9'
          className='ac-sidebar-v9'
          ref={drawerRef}
          role={mobile ? 'dialog' : undefined}
          aria-modal={mobile ? 'true' : undefined}
          aria-label={t('v9.navigation')}
          onKeyDown={handleDrawerKeyDown}
        >
          <div className='ac-sidebar-brand-v9'>
            <Link className='ac-brand-v9' to='/home' onClick={closeDrawer}>
              {brand}
            </Link>
            {mobile ? (
              <button
                className='ac-icon-button-v9'
                type='button'
                aria-label={t('header.close_menu')}
                onClick={closeDrawer}
              >
                <Icon name='close' />
              </button>
            ) : null}
          </div>
          <input
            className='ac-nav-search-v9'
            type='search'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('v9.searchNavigation')}
            aria-label={t('v9.searchNavigation')}
          />
          <nav className='ac-navigation-v9' aria-label={t('v9.navigation')}>
            {ACUVERSE_NAV_GROUPS_V9.map((group) => {
              const groupItems = filteredItems.filter(
                (item) => item.group === group
              );
              return groupItems.length ? (
                <section className='ac-nav-group-v9' key={group}>
                  <h2>{t('v9.groups.' + group)}</h2>
                  {groupItems.map((item) => (
                    <NavLink
                      className={({ isActive }) =>
                        'ac-nav-item-v9 ' +
                        (mobile
                          ? 'header-mobile-nav-item-v81'
                          : 'header-nav-item-v8') +
                        (isActive ? ' is-active' : '')
                      }
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      onClick={closeDrawer}
                    >
                      <Icon name={item.icon} />
                      <span>{navLabel(item.name)}</span>
                    </NavLink>
                  ))}
                </section>
              ) : null;
            })}
            {hierarchy.ready && filteredItems.length === 0 ? (
              <p className='ac-nav-empty-v9'>{t('v9.noNavigation')}</p>
            ) : null}
          </nav>
        </aside>
      ) : null}
      {mobile && drawerOpen && consolePage ? (
        <button
          className='ac-backdrop-v9'
          aria-label={t('header.close_menu')}
          tabIndex={-1}
          type='button'
          onClick={closeDrawer}
        />
      ) : null}
      <div className='ac-content-v9' ref={contentRef}>
        <main
          id='ac-main-v9'
          className={
            'ac-main-v9' + (pathname.includes('log') ? ' ac-main-wide-v9' : '')
          }
          tabIndex={-1}
        >
          {children}
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default AcuverseLayoutV9;
